import api from "../index";

// Types for analytics API
export interface DateRangeParams {
  startDate?: string;
  endDate?: string;
  days?: number;
}

export interface PaginationParams {
  page: number;
  perPage: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

export interface TimeSeriesParams extends DateRangeParams {
  metric: "users" | "events" | "tokens" | "generations" | "totalEvents";
  granularity?: "hour" | "day" | "week" | "month";
}

// Helper to convert days to date range
const daysToDateRange = (days?: number): { startDate?: string; endDate?: string } => {
  if (!days) return {};
  const endDate = new Date().toISOString().split("T")[0];
  const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
  return { startDate, endDate };
};

// ********** Dashboard Overview ********** //
/**
 * Get dashboard overview metrics
 * @param dateRange - Optional date range parameters
 */
export const getDashboardOverview = async (dateRange?: DateRangeParams) => {
  const params: Record<string, string> = {};

  // Convert days to date range if provided
  if (dateRange?.days) {
    const { startDate, endDate } = daysToDateRange(dateRange.days);
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
  } else {
    if (dateRange?.startDate) params.startDate = dateRange.startDate;
    if (dateRange?.endDate) params.endDate = dateRange.endDate;
  }

  const response = await api.get("/analytics/dashboard/overview", { params });

  // Transform response to expected format
  const { summary, topEvents, platformDistribution, deviceDistribution } = response.data;

  return {
    data: {
      totalEvents: summary?.totalEvents || 0,
      activeUsers: summary?.totalUniqueUsers || 0,
      totalTokens: summary?.totalTokens || 0,
      totalGenerations: summary?.totalGenerations || 0,
      totalUsers: summary?.totalNewUsers || 0,
      avgTokensPerGeneration: summary?.avgTokensPerGeneration || 0,
      growthRate: {
        events: summary?.eventGrowth || 0,
        tokens: summary?.tokenGrowth || 0,
        generations: summary?.generationGrowth || 0,
        users: summary?.userGrowth || 0,
      },
    },
    success: response.data.success,
  };
};

// ********** Time Series Data ********** //
/**
 * Get time series data for specific metrics
 * @param params - Time series parameters including metric type and granularity
 */
export const getTimeSeries = async (params: TimeSeriesParams) => {
  const queryParams: Record<string, string> = {
    metric: params.metric === "generations" ? "totalEvents" : params.metric,
  };

  // Convert days to date range if provided
  if (params.days) {
    const { startDate, endDate } = daysToDateRange(params.days);
    if (startDate) queryParams.startDate = startDate;
    if (endDate) queryParams.endDate = endDate;
  } else {
    if (params.startDate) queryParams.startDate = params.startDate;
    if (params.endDate) queryParams.endDate = params.endDate;
  }

  const response = await api.get("/analytics/dashboard/time-series", { params: queryParams });

  // Transform to expected format
  const timeSeriesData = response.data.data || [];

  return {
    data: {
      labels: timeSeriesData.map((item: any) => item.date || item.label),
      datasets: [{
        data: timeSeriesData.map((item: any) => item.count || item.value || 0),
      }],
    },
    success: response.data.success,
  };
};

// ********** Platform Distribution ********** //
/**
 * Get platform distribution analytics (web, mobile, extension, etc.)
 * @param dateRange - Optional date range parameters
 */
export const getPlatformDistribution = async (dateRange?: DateRangeParams) => {
  const params: Record<string, string> = {};

  if (dateRange?.days) {
    const { startDate, endDate } = daysToDateRange(dateRange.days);
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
  } else {
    if (dateRange?.startDate) params.startDate = dateRange.startDate;
    if (dateRange?.endDate) params.endDate = dateRange.endDate;
  }

  const response = await api.get("/analytics/dashboard/platform-distribution", { params });

  // Transform to expected format - server returns { counts: {}, percentages: {}, total }
  const distribution = response.data.distribution || {};
  const counts = distribution.counts || {};
  const percentages = distribution.percentages || {};

  // Convert object format to array format
  const platformArray = Object.keys(counts).map((platform) => ({
    platform: platform || "Unknown",
    count: counts[platform] || 0,
    percentage: parseFloat(percentages[platform]) || 0,
  }));

  return {
    data: platformArray,
    success: response.data.success,
  };
};

// ********** Provider Breakdown ********** //
/**
 * Get AI provider breakdown analytics (OpenAI, Anthropic, etc.)
 * @param dateRange - Optional date range parameters
 */
export const getProviderBreakdown = async (dateRange?: DateRangeParams) => {
  const params: Record<string, string> = {};

  if (dateRange?.days) {
    const { startDate, endDate } = daysToDateRange(dateRange.days);
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
  } else {
    if (dateRange?.startDate) params.startDate = dateRange.startDate;
    if (dateRange?.endDate) params.endDate = dateRange.endDate;
  }

  // Use top-events endpoint and filter for provider-related events
  const response = await api.get("/analytics/dashboard/top-events", { params });

  // Transform to expected format - group by provider if available
  const events = response.data.events || [];
  const totalCount = events.reduce((sum: number, e: any) => sum + (e.count || 0), 0);

  // For now, return the events as providers (this can be enhanced later)
  return {
    data: events.slice(0, 6).map((item: any, index: number) => ({
      provider: item.name || item.eventName || item._id || `Provider ${index + 1}`,
      count: item.count || 0,
      tokens: item.tokens || 0,
      percentage: totalCount > 0 ? ((item.count || 0) / totalCount * 100) : 0,
    })),
    success: response.data.success,
  };
};

// ********** User Activity ********** //
/**
 * Get top users by event activity
 * @param dateRange - Optional date range parameters
 * @param pagination - Pagination parameters
 */
export const getUserActivity = async (
  dateRange?: DateRangeParams,
  pagination?: PaginationParams
) => {
  const params: Record<string, string> = {};

  if (dateRange?.days) {
    const { startDate, endDate } = daysToDateRange(dateRange.days);
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
  } else {
    if (dateRange?.startDate) params.startDate = dateRange.startDate;
    if (dateRange?.endDate) params.endDate = dateRange.endDate;
  }

  if (pagination?.perPage) {
    params.limit = pagination.perPage.toString();
  }

  const response = await api.get("/analytics/dashboard/top-users", { params });

  return {
    data: {
      users: (response.data.users || []).map((user: any) => ({
        id: user.id,
        name: user.name || "Unknown User",
        email: user.email || "",
        avatar: user.avatar || null,
        plan: user.plan || "free",
        totalEvents: user.totalEvents || 0,
        eventTypes: user.eventTypes || 0,
        lastActivity: user.lastActivity,
        firstActivity: user.firstActivity,
      })),
      total: response.data.total || 0,
      totalPages: 1,
    },
    success: response.data.success,
  };
};

// ********** Geography ********** //
/**
 * Get geographic distribution of users and events
 * @param dateRange - Optional date range parameters
 */
export const getGeography = async (dateRange?: DateRangeParams) => {
  const params: Record<string, string> = {};

  if (dateRange?.days) {
    const { startDate, endDate } = daysToDateRange(dateRange.days);
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
  } else {
    if (dateRange?.startDate) params.startDate = dateRange.startDate;
    if (dateRange?.endDate) params.endDate = dateRange.endDate;
  }

  const response = await api.get("/analytics/dashboard/geo-distribution", { params });

  return {
    data: response.data.distribution || [],
    success: response.data.success,
  };
};

// ********** Real-time Events ********** //
/**
 * Get real-time events feed
 * @param limit - Number of events to fetch (default: 20)
 */
export const getRealtimeEvents = async (limit: number = 20) => {
  const response = await api.get("/analytics/dashboard/real-time", {
    params: { limit: limit.toString() },
  });

  // Transform to expected format - use 'name' field from server response
  const eventBreakdown = response.data.eventBreakdown || [];

  return {
    data: eventBreakdown.map((item: any) => ({
      id: item._id || Math.random().toString(),
      type: item.name || item.eventName || item._id || "unknown",
      timestamp: item.lastOccurred || item.timestamp || new Date().toISOString(),
      user: {
        name: item.userName || "Anonymous",
        avatar: item.userAvatar || null,
      },
      metadata: {
        tokens: item.tokens || 0,
        count: item.count || 0,
        uniqueUserCount: item.uniqueUserCount || 0,
      },
    })),
    success: response.data.success,
  };
};

// ********** Feature Usage ********** //
/**
 * Get feature usage breakdown
 * @param dateRange - Optional date range parameters
 */
export const getFeatureUsage = async (dateRange?: DateRangeParams) => {
  const params: Record<string, string> = {};

  if (dateRange?.days) {
    const { startDate, endDate } = daysToDateRange(dateRange.days);
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
  } else {
    if (dateRange?.startDate) params.startDate = dateRange.startDate;
    if (dateRange?.endDate) params.endDate = dateRange.endDate;
  }

  // Use top-events endpoint for feature breakdown
  const response = await api.get("/analytics/dashboard/top-events", { params });

  const events = response.data.events || [];
  const totalCount = events.reduce((sum: number, e: any) => sum + (e.count || 0), 0);

  return {
    data: events.slice(0, 10).map((item: any) => ({
      feature: item.name || item.eventName || item._id || "Unknown",
      count: item.count || 0,
      percentage: totalCount > 0 ? ((item.count || 0) / totalCount * 100) : 0,
      trend: item.trend || 0,
    })),
    success: response.data.success,
  };
};

// ********** Device Analytics ********** //
/**
 * Get device analytics (browser, OS, device type breakdown)
 * @param dateRange - Optional date range parameters
 */
export const getDeviceAnalytics = async (dateRange?: DateRangeParams) => {
  const params: Record<string, string> = {};

  if (dateRange?.days) {
    const { startDate, endDate } = daysToDateRange(dateRange.days);
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
  } else {
    if (dateRange?.startDate) params.startDate = dateRange.startDate;
    if (dateRange?.endDate) params.endDate = dateRange.endDate;
  }

  const response = await api.get("/analytics/dashboard/device-distribution", { params });

  return {
    data: {
      browsers: response.data.browsers || [],
      operatingSystems: response.data.operatingSystems || [],
      deviceTypes: response.data.deviceTypes || [],
      total: response.data.total || 0,
    },
    success: response.data.success,
  };
};

// ********** Browser Distribution ********** //
/**
 * Get browser distribution analytics
 * @param dateRange - Optional date range parameters
 */
export const getBrowserDistribution = async (dateRange?: DateRangeParams) => {
  const params: Record<string, string> = {};

  if (dateRange?.days) {
    const { startDate, endDate } = daysToDateRange(dateRange.days);
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
  } else {
    if (dateRange?.startDate) params.startDate = dateRange.startDate;
    if (dateRange?.endDate) params.endDate = dateRange.endDate;
  }

  const response = await api.get("/analytics/dashboard/browser-distribution", { params });

  const distribution = response.data.distribution || [];
  const total = distribution.reduce((sum: number, b: any) => sum + (b.count || 0), 0);

  return {
    data: distribution.map((item: any) => ({
      browser: item.browser || item._id || "Unknown",
      count: item.count || 0,
      percentage: total > 0 ? ((item.count || 0) / total * 100) : 0,
    })),
    success: response.data.success,
  };
};

// ********** OS Distribution ********** //
/**
 * Get operating system distribution analytics
 * @param dateRange - Optional date range parameters
 */
export const getOSDistribution = async (dateRange?: DateRangeParams) => {
  const params: Record<string, string> = {};

  if (dateRange?.days) {
    const { startDate, endDate } = daysToDateRange(dateRange.days);
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
  } else {
    if (dateRange?.startDate) params.startDate = dateRange.startDate;
    if (dateRange?.endDate) params.endDate = dateRange.endDate;
  }

  const response = await api.get("/analytics/dashboard/os-distribution", { params });

  const distribution = response.data.distribution || [];
  const total = distribution.reduce((sum: number, o: any) => sum + (o.count || 0), 0);

  return {
    data: distribution.map((item: any) => ({
      os: item.os || item._id || "Unknown",
      count: item.count || 0,
      percentage: total > 0 ? ((item.count || 0) / total * 100) : 0,
    })),
    success: response.data.success,
  };
};

// ********** Event Breakdown ********** //
/**
 * Get detailed event breakdown by category
 * @param dateRange - Optional date range parameters
 */
export const getEventBreakdown = async (dateRange?: DateRangeParams) => {
  const params: Record<string, string> = {};

  if (dateRange?.days) {
    const { startDate, endDate } = daysToDateRange(dateRange.days);
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
  } else {
    if (dateRange?.startDate) params.startDate = dateRange.startDate;
    if (dateRange?.endDate) params.endDate = dateRange.endDate;
  }

  const response = await api.get("/analytics/dashboard/top-events", { params: { ...params, limit: "50" } });

  const events = response.data.events || [];
  const totalCount = events.reduce((sum: number, e: any) => sum + (e.count || 0), 0);

  // Group events by category (prefix before the dot)
  const categories: Record<string, { count: number; events: any[] }> = {};
  events.forEach((event: any) => {
    const eventName = event.name || event._id || "unknown";
    const category = eventName.split(".")[0] || "other";
    if (!categories[category]) {
      categories[category] = { count: 0, events: [] };
    }
    categories[category].count += event.count || 0;
    categories[category].events.push({
      name: eventName,
      count: event.count || 0,
      percentage: totalCount > 0 ? ((event.count || 0) / totalCount * 100) : 0,
    });
  });

  return {
    data: {
      events: events.map((item: any) => ({
        name: item.name || item._id || "Unknown",
        count: item.count || 0,
        percentage: totalCount > 0 ? ((item.count || 0) / totalCount * 100) : 0,
        uniqueUsers: item.uniqueUserCount || 0,
      })),
      categories: Object.entries(categories).map(([name, data]) => ({
        name,
        count: data.count,
        percentage: totalCount > 0 ? (data.count / totalCount * 100) : 0,
        eventCount: data.events.length,
      })).sort((a, b) => b.count - a.count),
      total: totalCount,
    },
    success: response.data.success,
  };
};

// ********** Export Analytics ********** //
/**
 * Export analytics data to CSV/Excel
 * @param type - Type of export (overview, users, events)
 * @param dateRange - Date range for export
 */
export const exportAnalytics = async (
  type: "overview" | "users" | "events",
  dateRange?: DateRangeParams
) => {
  const params: Record<string, string> = { type };

  if (dateRange?.days) {
    const { startDate, endDate } = daysToDateRange(dateRange.days);
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
  } else {
    if (dateRange?.startDate) params.startDate = dateRange.startDate;
    if (dateRange?.endDate) params.endDate = dateRange.endDate;
  }

  // For now, return the overview data as a blob
  const response = await api.get("/analytics/dashboard/overview", { params });

  // Convert to CSV blob
  const csvContent = JSON.stringify(response.data, null, 2);
  const blob = new Blob([csvContent], { type: "application/json" });

  return {
    data: blob,
    success: true,
  };
};
